import { Injectable } from '@angular/core';
import { UserData } from './my-data.types';
import { lastValueFrom, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { UserService } from 'app/core/services/user.service';
import { HippoConfirmationService } from '@hippo/services/confirmation';
import { translate, TranslocoService } from '@jsverse/transloco';
import { SharedService } from 'app/core/services/shared.service';

@Injectable({
    providedIn: 'root',
})
export class MyDataService {
    userData: UserData;
    originalUserData: UserData;
    newHyperlinks = 0;
    switchToGeneralTab = new Subject<void>();
    openedMenus: boolean[];
    originalOpenedMenus: boolean[];
    selectedTab: string = 'Allgemein';

    // Date validation for my-data-general
    wrongBirthday = false;
    birthdayInputValue: string = '';
    wrongEntryDate = false;
    entryDateInputValue: string = '';

    // Date validation for my-data-passport
    wrongIssueDate = false;
    issueDateInputValue: string = '';
    wrongIssueDateValid = false;
    issueDateValidInputValue: string = '';
    wrongIssueDate2 = false;
    issueDate2InputValue: string = '';
    wrongIssueDateValid2 = false;
    issueDateValid2InputValue: string = '';

    // Date validation for my-data-driving-license
    wrongCardValid = false;
    cardValidInputValue: string = '';
    wrongFqnCardValid = false;
    fqnCardValidInputValue: string = '';

    // Date validation for my-data-travel-cards
    wrongRailcardValid = false;
    railcardValidInputValue: string = '';

    hasPageAccess = true;

    constructor(
        private httpClient: HttpClient,
        public userService: UserService,
        private hippoConfirmationService: HippoConfirmationService,
        private translocoService: TranslocoService,
        private sharedService: SharedService
    ) {}

    async loadUserData() {
        try {
            const formData = new FormData();
            formData.append('submit_option', 'opt_reset');
            const data = await lastValueFrom(
                this.httpClient.post<any>(
                    `hippo/navigator.php?page=pages/users/User_data.php&uid=${this.userService.user.uid}&page_type=json`,
                    formData,
                    {
                        observe: 'response',
                    }
                )
            );

            if (data.body.status) {
                this.userData = JSON.parse(JSON.stringify(data.body.result));
                this.originalUserData = JSON.parse(
                    JSON.stringify(this.userData)
                );
            } else {
                this.sharedService.showDataNotLoaded(
                    data,
                    'MyDataService',
                    'loadUserData'
                );
            }
            this.translocoService.setActiveLang(
                this.userData.user_settings_language
            );
            this.openedMenus = Array(13).fill(false);
            this.originalOpenedMenus = Array(13).fill(false);
            let userSettingsMenu = this.userData.user_settings_menu;

            if (userSettingsMenu) {
                const userMenuSettingsSplitted = userSettingsMenu.split(';');

                userMenuSettingsSplitted.forEach(menu => {
                    this.openedMenus[+menu] = true;
                    this.originalOpenedMenus[+menu] = true;
                });
            }
            this.hasPageAccess = true;
        } catch (error) {
            console.log(error);
            const result = error?.error?.result;

            if (
                result === 'page access denied' ||
                result?.message === 'page access denied'
            ) {
                this.hasPageAccess = false;
            }
            this.sharedService.showDataNotLoaded(
                error,
                'MyDataService',
                'loadUserData (2)'
            );
        }
    }

    async saveUserData() {
        const formData = new FormData();
        formData.append('submit_option', 'opt_save');
        formData.append('usr_salutation', this.userData.usr_salutation ?? '');
        formData.append('usr_first_name', this.userData.usr_first_name ?? '');
        formData.append('usr_last_name', this.userData.usr_last_name ?? '');
        formData.append('usr_e_mail', this.userData.usr_e_mail ?? '');
        formData.append('usr_adr_id', this.userData.usr_adr_id ?? '');
        formData.append('usr_street', this.userData.usr_street ?? '');
        formData.append('usr_zip_code', this.userData.usr_zip_code ?? '');
        formData.append('usr_city', this.userData.usr_city ?? '');
        formData.append('usr_country', this.userData.usr_country ?? '');
        formData.append(
            'usr_phone_number',
            this.userData.usr_phone_number ?? ''
        );
        formData.append(
            'usr_mobile_number',
            this.userData.usr_mobile_number ?? ''
        );
        formData.append('usr_fax_number', this.userData.usr_fax_number ?? '');
        formData.append('usr_birthday', this.userData.usr_birthday ?? '');
        formData.append('usr_entry_date', this.userData.usr_entry_date ?? '');

        formData.append('uad_id', this.userData.uad_id ?? '');
        formData.append('uad_shirt_size', this.userData.uad_shirt_size ?? '');
        formData.append('uad_correct_fit', this.userData.uad_correct_fit ?? '');
        formData.append(
            'uad_sleeve_length',
            this.userData.uad_sleeve_length ?? ''
        );
        formData.append(
            'uad_polo_shirt_size',
            this.userData.uad_polo_shirt_size ?? ''
        );
        formData.append(
            'uad_polo_shirt_quantity_required',
            this.userData.uad_polo_shirt_quantity_required ?? ''
        );
        formData.append(
            'uad_polo_shirt_quantity_owned',
            this.userData.uad_polo_shirt_quantity_owned ?? ''
        );
        formData.append('uad_has_watch', this.userData.uad_has_watch ?? '');
        formData.append('uad_watch_model', this.userData.uad_watch_model ?? '');

        formData.append('udl_id', this.userData.udl_id ?? '');
        formData.append(
            'udl_drivers_licence_number',
            this.userData.udl_drivers_licence_number ?? ''
        );
        formData.append(
            'udl_has_licence_b',
            this.userData.udl_has_licence_b ?? ''
        );
        formData.append(
            'udl_has_licence_be',
            this.userData.udl_has_licence_be ?? ''
        );
        formData.append(
            'udl_has_licence_c1',
            this.userData.udl_has_licence_c1 ?? ''
        );
        formData.append(
            'udl_licence_c1_date_to',
            this.userData.udl_licence_c1_date_to ?? ''
        );
        formData.append(
            'udl_licence_c1_restrictions',
            this.userData.udl_licence_c1_restrictions ?? ''
        );
        formData.append(
            'udl_has_licence_c1e',
            this.userData.udl_has_licence_c1e ?? ''
        );
        formData.append(
            'udl_licence_c1e_date_to',
            this.userData.udl_licence_c1e_date_to ?? ''
        );
        formData.append(
            'udl_licence_c1e_restrictions',
            this.userData.udl_licence_c1e_restrictions ?? ''
        );
        formData.append(
            'udl_has_licence_c',
            this.userData.udl_has_licence_c ?? ''
        );
        formData.append(
            'udl_licence_c_date_to',
            this.userData.udl_licence_c_date_to ?? ''
        );
        formData.append(
            'udl_licence_c_restrictions',
            this.userData.udl_licence_c_restrictions ?? ''
        );
        formData.append(
            'udl_has_licence_ce',
            this.userData.udl_has_licence_ce ?? ''
        );
        formData.append(
            'udl_licence_ce_date_to',
            this.userData.udl_licence_ce_date_to ?? ''
        );
        formData.append(
            'udl_licence_ce_restrictions',
            this.userData.udl_licence_ce_restrictions ?? ''
        );
        formData.append(
            'udl_has_driver_card',
            this.userData.udl_has_driver_card ?? ''
        );
        formData.append(
            'udl_driver_card_number',
            this.userData.udl_driver_card_number ?? ''
        );
        formData.append(
            'udl_driver_card_date_to',
            this.userData.udl_driver_card_date_to ?? ''
        );
        formData.append(
            'udl_has_driver_qualification_card',
            this.userData.udl_has_driver_qualification_card ?? ''
        );
        formData.append(
            'udl_driver_qualification_card_number',
            this.userData.udl_driver_qualification_card_number ?? ''
        );
        formData.append(
            'udl_driver_qualification_card_date_to',
            this.userData.udl_driver_qualification_card_date_to ?? ''
        );

        formData.append(
            'uad_global_entry_usa',
            this.userData.uad_global_entry_usa ?? ''
        );

        formData.append(
            '0_upd_id',
            this.userData.usr_passport?.[0]?.upd_id ?? ''
        );
        formData.append(
            '0_upd_number',
            this.userData.usr_passport?.[0]?.upd_number ?? ''
        );
        formData.append(
            '0_upd_last_name',
            this.userData.usr_passport?.[0]?.upd_last_name ?? ''
        );
        formData.append(
            '0_upd_first_name',
            this.userData.usr_passport?.[0]?.upd_first_name ?? ''
        );
        formData.append(
            '0_upd_nationality',
            this.userData.usr_passport?.[0]?.upd_nationality ?? ''
        );
        formData.append(
            '0_upd_sex',
            this.userData.usr_passport?.[0]?.upd_sex ?? ''
        );
        formData.append(
            '0_upd_birth_place',
            this.userData.usr_passport?.[0]?.upd_birth_place ?? ''
        );
        formData.append(
            '0_upd_issue_place',
            this.userData.usr_passport?.[0]?.upd_issue_place ?? ''
        );
        formData.append(
            '0_upd_issue_date',
            this.userData.usr_passport?.[0]?.upd_issue_date ?? ''
        );
        formData.append(
            '0_upd_valid_to_date',
            this.userData.usr_passport?.[0]?.upd_valid_to_date ?? ''
        );
        formData.append(
            '1_upd_id',
            this.userData.usr_passport?.[1]?.upd_id ?? ''
        );
        formData.append(
            '1_upd_number',
            this.userData.usr_passport?.[1]?.upd_number ?? ''
        );
        formData.append(
            '1_upd_last_name',
            this.userData.usr_passport?.[1]?.upd_last_name ?? ''
        );
        formData.append(
            '1_upd_first_name',
            this.userData.usr_passport?.[1]?.upd_first_name ?? ''
        );
        formData.append(
            '1_upd_nationality',
            this.userData.usr_passport?.[1]?.upd_nationality ?? ''
        );
        formData.append(
            '1_upd_sex',
            this.userData.usr_passport?.[1]?.upd_sex ?? ''
        );
        formData.append(
            '1_upd_birth_place',
            this.userData.usr_passport?.[1]?.upd_birth_place ?? ''
        );
        formData.append(
            '1_upd_issue_place',
            this.userData.usr_passport?.[1]?.upd_issue_place ?? ''
        );
        formData.append(
            '1_upd_issue_date',
            this.userData.usr_passport?.[1]?.upd_issue_date ?? ''
        );
        formData.append(
            '1_upd_valid_to_date',
            this.userData.usr_passport?.[1]?.upd_valid_to_date ?? ''
        );
        formData.append(
            'upc_id_1_0',
            this.userData.travel_cards?.[1]?.[0]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_1_0',
            this.userData.travel_cards?.[1]?.[0]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_1_0',
            this.userData.travel_cards?.[1]?.[0]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_1_1',
            this.userData.travel_cards?.[1]?.[1]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_1_1',
            this.userData.travel_cards?.[1]?.[1]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_1_1',
            this.userData.travel_cards?.[1]?.[1]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_1_2',
            this.userData.travel_cards?.[1]?.[2]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_1_2',
            this.userData.travel_cards?.[1]?.[2]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_1_2',
            this.userData.travel_cards?.[1]?.[2]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_1_3',
            this.userData.travel_cards?.[1]?.[3]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_1_3',
            this.userData.travel_cards?.[1]?.[3]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_1_3',
            this.userData.travel_cards?.[1]?.[3]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_1_4',
            this.userData.travel_cards?.[1]?.[4]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_1_4',
            this.userData.travel_cards?.[1]?.[4]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_1_4',
            this.userData.travel_cards?.[1]?.[4]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_2_0',
            this.userData.travel_cards?.[2]?.[0]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_2_0',
            this.userData.travel_cards?.[2]?.[0]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_2_0',
            this.userData.travel_cards?.[2]?.[0]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_2_1',
            this.userData.travel_cards?.[2]?.[1]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_2_1',
            this.userData.travel_cards?.[2]?.[1]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_2_1',
            this.userData.travel_cards?.[2]?.[1]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_2_2',
            this.userData.travel_cards?.[2]?.[2]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_2_2',
            this.userData.travel_cards?.[2]?.[2]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_2_2',
            this.userData.travel_cards?.[2]?.[2]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_2_3',
            this.userData.travel_cards?.[2]?.[3]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_2_3',
            this.userData.travel_cards?.[2]?.[3]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_2_3',
            this.userData.travel_cards?.[2]?.[3]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_3_0',
            this.userData.travel_cards?.[3]?.[0]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_3_0',
            this.userData.travel_cards?.[3]?.[0]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_3_0',
            this.userData.travel_cards?.[3]?.[0]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_3_1',
            this.userData.travel_cards?.[3]?.[1]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_3_1',
            this.userData.travel_cards?.[3]?.[1]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_3_1',
            this.userData.travel_cards?.[3]?.[1]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_3_2',
            this.userData.travel_cards?.[3]?.[2]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_3_2',
            this.userData.travel_cards?.[3]?.[2]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_3_2',
            this.userData.travel_cards?.[3]?.[2]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_3_3',
            this.userData.travel_cards?.[3]?.[3]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_3_3',
            this.userData.travel_cards?.[3]?.[3]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_3_3',
            this.userData.travel_cards?.[3]?.[3]?.upc_number ?? ''
        );
        formData.append(
            'upc_id_3_4',
            this.userData.travel_cards?.[3]?.[4]?.upc_id ?? ''
        );
        formData.append(
            'ucp_id_3_4',
            this.userData.travel_cards?.[3]?.[4]?.ucp_id ?? ''
        );
        formData.append(
            'upc_number_3_4',
            this.userData.travel_cards?.[3]?.[4]?.upc_number ?? ''
        );
        formData.append(
            '0_utc_id',
            this.userData.usr_train_card?.[0]?.utc_id ?? ''
        );
        formData.append(
            '0_utc_company',
            this.userData.usr_train_card?.[0]?.utc_company ?? ''
        );
        formData.append(
            '0_utc_card_type',
            this.userData.usr_train_card?.[0]?.utc_card_type ?? ''
        );
        formData.append(
            '0_utc_number',
            this.userData.usr_train_card?.[0]?.utc_number ?? ''
        );
        formData.append(
            '0_utc_date_to',
            this.userData.usr_train_card?.[0]?.utc_date_to ?? ''
        );

        formData.append('uad_flight_seat', this.userData.uad_flight_seat ?? '');
        formData.append('uad_flight_meal', this.userData.uad_flight_meal ?? '');
        formData.append(
            'uad_flight_home_airport_1',
            this.userData.uad_flight_home_airport_1 ?? ''
        );
        formData.append(
            'uad_flight_home_airport_2',
            this.userData.uad_flight_home_airport_2 ?? ''
        );
        formData.append(
            'uad_flight_home_airport_3',
            this.userData.uad_flight_home_airport_3 ?? ''
        );
        formData.append(
            'uad_flight_alternative_airport_1',
            this.userData.uad_flight_alternative_airport_1 ?? ''
        );
        formData.append(
            'uad_flight_alternative_airport_2',
            this.userData.uad_flight_alternative_airport_2 ?? ''
        );
        formData.append(
            'uad_flight_alternative_airport_3',
            this.userData.uad_flight_alternative_airport_3 ?? ''
        );

        this.userData.usr_hyperlinks.forEach(hyperlink => {
            formData.append('txt_name_' + hyperlink.hlk_id, hyperlink.hlk_name);
            formData.append('txt_link_' + hyperlink.hlk_id, hyperlink.hlk_link);
            formData.append(
                'txt_remarks_' + hyperlink.hlk_id,
                hyperlink.hlk_remarks
            );
        });
        formData.append(
            'submit_link_ids',
            this.userData.usr_hyperlinks
                .map(hyperlink => hyperlink.hlk_id)
                .toString()
                .replaceAll(',', ';')
        );

        formData.append(
            'user_settings_language',
            this.userData.user_settings_language
        );
        formData.append(
            'user_settings_color',
            this.userData.user_settings_color
        );
        formData.append('submit_active_menu', '0;1;2;3;4;5;6;7;8;9;10;11;12');
        formData.append(
            'uss_menu_0',
            (+(this.openedMenus[0] ?? false)).toString()
        );
        formData.append(
            'uss_menu_1',
            (+(this.openedMenus[1] ?? false)).toString()
        );
        formData.append(
            'uss_menu_2',
            (+(this.openedMenus[2] ?? false)).toString()
        );
        formData.append(
            'uss_menu_3',
            (+(this.openedMenus[3] ?? false)).toString()
        );
        formData.append(
            'uss_menu_4',
            (+(this.openedMenus[4] ?? false)).toString()
        );
        formData.append(
            'uss_menu_5',
            (+(this.openedMenus[5] ?? false)).toString()
        );
        formData.append(
            'uss_menu_6',
            (+(this.openedMenus[6] ?? false)).toString()
        );
        formData.append(
            'uss_menu_7',
            (+(this.openedMenus[7] ?? false)).toString()
        );
        formData.append(
            'uss_menu_8',
            (+(this.openedMenus[8] ?? false)).toString()
        );
        formData.append(
            'uss_menu_9',
            (+(this.openedMenus[9] ?? false)).toString()
        );
        formData.append(
            'uss_menu_10',
            (+(this.openedMenus[10] ?? false)).toString()
        );
        formData.append(
            'uss_menu_11',
            (+(this.openedMenus[11] ?? false)).toString()
        );
        formData.append(
            'uss_menu_12',
            (+(this.openedMenus[12] ?? false)).toString()
        );
        // formData.append('uad_blouse_size', this.userData.uad_blouse_size);

        try {
            await lastValueFrom(
                this.httpClient.post(
                    `hippo/navigator.php?page=pages/users/User_data.php&uid=${this.userService.user.uid}&page_type=json`,
                    formData,
                    {
                        observe: 'response',
                    }
                )
            );

            const confirmation = this.hippoConfirmationService.open({
                title: 'Information',
                message: translate('common.save_success'),
                icon: { show: true, color: 'info', name: 'info' },
                actions: {
                    confirm: {
                        label: 'OK',
                        color: 'primary',
                    },
                    cancel: {
                        show: false,
                    },
                },
                dismissible: false,
            });
            if (
                this.userData.user_settings_language !==
                this.originalUserData.user_settings_language
            ) {
                this.sharedService.apiSystemLanguage =
                    this.userData.user_settings_language;
            }
            this.newHyperlinks = 0;
            // forces the opended Component to reload ngOnInit
            const temp = this.selectedTab;
            this.selectedTab = '';
            setTimeout(async () => {
                await this.loadUserData();
                this.selectedTab = temp;
            }, 100);
        } catch (error) {
            console.log(error);
            if (
                typeof error?.error?.result === 'string' &&
                error.error.result.includes('field type error:')
            ) {
                const fieldTypeErrors = error.error.result
                    .replace('field type error:', '')
                    .split(',');
                let displayedErrors: string[] = [];
                fieldTypeErrors.forEach(ftError => {
                    switch (ftError.trim()) {
                        case 'usr_first_name':
                            displayedErrors.push(
                                translate('pusd.tab_0_first_name')
                            );
                            break;
                        case 'usr_last_name':
                            displayedErrors.push(
                                translate('pusd.tab_0_last_name')
                            );
                            break;
                        case 'usr_e_mail':
                            displayedErrors.push('E-mail');
                            break;
                        case 'usr_zip_code':
                            displayedErrors.push(
                                translate('pusd.tab_0_zip_code')
                            );
                            break;
                        case 'uad_polo_shirt_quantity_required':
                            displayedErrors.push(
                                translate('pusd.tab_2_number_polo_required')
                            );
                            break;
                        case 'uad_polo_shirt_quantity_owned':
                            displayedErrors.push(
                                translate('pusd.tab_2_number_polo_exists')
                            );
                            break;

                        default:
                            displayedErrors.push(ftError.trim());
                            break;
                    }
                });

                this.sharedService.showWrongFilledFields(displayedErrors);
            } else {
                this.sharedService.showDataNotSaved(
                    error,
                    'MyDataService',
                    'saveUserData'
                );
            }
        }
    }

    async removeHyperlink(hyperlinkId: string): Promise<boolean> {
        const formData = new FormData();
        formData.append('submit_option', 'opt_remove_link');
        formData.append('submit_value', hyperlinkId);
        try {
            await lastValueFrom(
                this.httpClient.post(
                    `hippo/navigator.php?page=pages/users/User_data.php&uid=${this.userService.user.uid}&page_type=json`,
                    formData,
                    {
                        observe: 'response',
                    }
                )
            );
            return true;
        } catch (error) {
            console.log(error);
            const message =
                this.translocoService.getActiveLang() === 'de'
                    ? 'Es ist ein Fehler aufgetreten. Der Hyperlink konnte nicht gelöscht werden. Prüfen Sie die Internetverbindung. Bei weiteren Problemen wenden Sie sich an ihren Systemadministrator!'
                    : 'An error has occurred. The hyperlink could not be deleted. Check your Internet connection. If you have further problems, contact your system administrator!';
            const confirmation = this.hippoConfirmationService.open({
                title: 'Information',
                message,
                icon: { show: true, color: 'error' },
                actions: {
                    confirm: {
                        label: 'OK',
                        color: 'primary',
                    },
                    cancel: {
                        show: false,
                    },
                },
                dismissible: false,
            });
            return false;
        }
    }
}
